import React, { useState } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

interface AddCustomItemDialogProps {
  onAddItem: (item: { name: string; price: number }) => void;
}

export function AddCustomItemDialog({ onAddItem }: AddCustomItemDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  const validateName = (value: string) => {
    const namePattern = /^[A-Za-z0-9\s]+$/;
    if (!namePattern.test(value)) {
      setNameError('Only letters, numbers, and spaces are allowed (no hyphens or special characters)');
    } else {
      setNameError(null);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    validateName(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedPrice = parseFloat(price);
    if (!name.trim()) {
      setNameError('Item name is required');
      return;
    }
    if (isNaN(parsedPrice)) {
      toast.error('Price must be a valid number');
      return;
    }
    if (parsedPrice < 0) {
      toast.error('Price must be a non-negative number');
      return;
    }
    if (!nameError && !isNaN(parsedPrice) && parsedPrice >= 0) {
      const item = { name: name.trim(), price: parsedPrice };
      console.log('Submitting custom item:', item);
      onAddItem(item);
      setName('');
      setPrice('');
      setNameError(null);
      setIsOpen(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full min-w-0 justify-center px-2 text-xs whitespace-nowrap overflow-hidden text-ellipsis">
          <Plus className="mr-1 h-4 w-4 shrink-0" />
          <span className="truncate">Custom Item</span>
        </Button>
      </DialogTrigger>
      <DialogContent aria-describedby="add-custom-item-description">
        <DialogHeader>
          <DialogTitle>Add Custom Item</DialogTitle>
          <DialogDescription id="add-custom-item-description">
            Enter the details for the custom item to add it to the sale.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="custom-name" className="text-sm font-medium">
              Item Name
            </label>
            <Input
              id="custom-name"
              value={name}
              onChange={handleNameChange}
              placeholder="Enter item name (e.g., Custom Widget)"
              required
            />
            {nameError && (
              <p className="text-sm text-red-500 mt-1">{nameError}</p>
            )}
          </div>
          <div>
            <label htmlFor="custom-price" className="text-sm font-medium">
              Price
            </label>
            <Input
              id="custom-price"
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={e => setPrice(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={!!nameError}>
            Add Item
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
